'use client';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@ahafood/ui/components/avatar';
import { Button } from '@ahafood/ui/components/button';
import { Sheet, SheetContent, SheetTitle } from '@ahafood/ui/components/sheet';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { motion } from 'framer-motion';
import { ExpandIcon, RotateCcw, XIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { LoginDialog } from '../fb-login';
import Chat from '@/app/[locale]/chat/page';
import { ROUTES } from '@/config/router';
import { useChatThread } from '@/hooks/use-chat-thread';
import { usePathname } from '@/i18n/routing';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile';
import { useAuthStore } from '@/store/use-auth-store';

export const defaultSuggestionsValue = [
  'Tư vấn món ngon giá rẻ có freeship',
  'SOLO MEAL có deal ăn một mình siêu hời',
  'Gợi ý món hot trong SOLO MEAL',
  // 'Nạp đồ ngọt chữa lành tâm hồn 🍰',
  // 'Xả stress bằng đồ cay xé lòng 🌶️',
  // 'Lấp đầy trống rỗng bằng trà sữa 🧋',
  // 'Cần một tô bún/phở nóng 🍜',
  // 'Tìm món ăn vặt cho ngày chằm Zn',
  // 'Chạy deadline với cơm văn phòng 💻',
  // 'Giữ dáng healthy với các món salad 🥗',
  // 'Nạp năng lượng bằng đồ ăn vặt 🍟',
  // 'Đặt cà phê/trà đậm vị cho não tỉnh ☕',
  // 'Nạp đường gấp với list chè/bánh ngọt 🍮',
  // 'Vitamin Sea từ các món ốc/hải sản 🐚',
  // 'Lương về, flex ngay bữa hải sản! 💸',
  // 'Vượt khó cuối tháng với món dưới 50k 💰',
  // 'Gợi ý đặc sản vùng miền lạ miệng',
  // 'Cần món gì bùng nổ vị giác? (Món Thái/Ấn) 🔥',
  // 'Nhẹ nhàng thanh lọc với các món cuốn/gỏi 🥬',
  // 'Tìm món tráng miệng cho đời bớt đắng 🍮',
  // 'Ăn gì cho hạ hỏa cơn giận? (Gợi ý chè/kem) 🧊',
  // 'Giải rượu thần tốc bằng phở/bún bò 😵',
  // 'Đãi cả nhà một bữa đặc sản vùng miền 👨‍👩‍👧‍👦',
  // 'Hẹn hò? Thử ngay các món Nhật/Hàn lãng mạn 🍣',
  // 'Ăn gì để có sức cày view cho idol?',
  // 'Tìm món ăn chống buồn ngủ',
  // 'Ăn gì để flex lên story cháy nhất? 🤳',
  // 'Truy tìm món ngon hết nước chấm 💯',
  // 'Vũ trụ mách bảo bạn phải ăn món này 🌌',
  // 'Món ăn cho người thành công (ăn gì cũng được) 🏆',
  // 'Thử vận may với một món ngẫu nhiên 🎲',
  // 'Bói món ăn theo cung hoàng đạo của bạn ♈',
  // 'Gợi ý món ăn Instagrammable nhất 📸',
  // 'Ăn gì cho nó keo lỳ? 💅',
  // 'Ăn gì để có cheap moment với crush?',
  // 'Tìm món sang chảnh nhưng giá hạt dẻ',
  // 'Ăn gì để thấy tiền mình đáng giá?',
  // 'Cần đồ nhắm cày phim xuyên đêm 🍿',
  // 'Ăn gì để tăng level đẹp trai/xinh gái? 💅',
  // 'Cần một ly nước ép detox tâm hồn 🍓',
  // 'Gợi ý các món dimsum/há cảo nóng hổi',
  // 'Xân biển hông? (đồ chiên á…)',
  // 'Thèm chè khúc bạch/chè dưỡng nhan',
  // 'Tìm món ngon bổ rẻ cho sinh viên',
  // 'Team ít tiền nhưng vẫn thích ăn sang',
  // 'Bữa ăn của "phú bà/phú ông" tương lai',
  // "Tìm các món 'visual' như em xinh say hi 💅",
  // 'Ăn gì cũng được!!!',
  // 'Ăn gì thì không mập???',
  // 'Ăn gì đẹp da?',
  // 'Người già nên ăn gì?',
  // 'Ăn gì để tâm trạng vui vẻ??',
  // 'Gợi ý món phong thủy cho Nhân Mã hôm nay',
];

// eslint-disable-next-line sonarjs/cognitive-complexity
export function ChatBot() {
  const isMobile = useIsMobile();
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { user, isAuthenticated, isLoading } = useAuthStore();
  const { threadId, restartThread } = useChatThread(
    user?.cunstomerId ?? null,
    null
  );
  const pathName = usePathname();

  useEffect(() => {
    if (isAuthenticated) {
      setShowLoginDialog(false);
    }
  }, [isAuthenticated]);

  const [isVisible, setIsVisible] = useState(false);
  if (typeof window !== 'undefined') {
    window.setIsVisible = setIsVisible;
  }
  const [isExpanded, setIsExpanded] = useState(false);

  const ChatHeader = () => (
    <div className="p-3 z-10 w-full items-center shrink-0 overflow-inherit color-inherit subpixel-antialiased rounded-t-large flex gap-3 justify-between bg-gradient-to-tr from-blue-10 to-red-20/80 pb-8">
      <div className="flex items-center gap-3 text-white">
        <Avatar className="size-10">
          <AvatarImage
            src={`${assetPrefix}/static/images/new-bot-avt.webp`}
            alt={'chat bot image'}
          />
          <AvatarFallback className="text-primary-foreground overflow-hidden size-14 mt-4 scale-125">
            Robot
          </AvatarFallback>
        </Avatar>
        <div className="text-secondary-50">
          <h3 className="font-bold text-sm">Amori</h3>
          <p className="text-xs font-thin">Solo Meals và Freeship</p>
        </div>
      </div>
      <div className="flex justify-end text-secondary-50">
        <Button
          variant="ghost"
          className="text-secondary-50 hover:bg-transparent"
          size="icon"
          onClick={() => {
            trackEvent('chatbot_event', {
              action_time: Math.floor(Date.now() / 1000),
              button_name: 'chatbot_restart',
              timestamp: Date.now(),
              user_id: user?.cunstomerId,
              thread_id: threadId,
            });
            restartThread();
          }}
        >
          <RotateCcw className="size-4" />
        </Button>
        {!isMobile && (
          <Button
            variant="ghost"
            className="text-secondary-50 hover:bg-transparent"
            size="icon"
            onClick={toggleExpand}
            id="webie-expand"
          >
            <ExpandIcon />
          </Button>
        )}
        <Button
          variant="ghost"
          className="text-secondary-50 hover:bg-transparent"
          size="icon"
          onClick={() => setIsVisible(false)}
          id="webie-close"
        >
          <XIcon />
        </Button>
      </div>
    </div>
  );

  const UnAuthen = () => (
    <div className="flex flex-col items-center justify-center h-full gap-4 bg-white rounded-t-xl">
      <Avatar className="size-40">
        <AvatarImage
          src={`${assetPrefix}/static/images/robot-cook.webp`}
          alt="AI Bot"
        />
        <AvatarFallback className="rounded-none">AI Bot</AvatarFallback>
      </Avatar>
      <div className="text-xl font-bold text-blue-60">
        Đăng nhập để chat với trợ lý AI
      </div>
      <Button
        size="lg"
        className="bg-blue-60 text-white rounded-full px-6 py-2 font-bold"
        onClick={() => {
          setShowLoginDialog(true);
        }}
      >
        Đăng nhập
      </Button>
    </div>
  );

  const toggleChat = () => {
    trackEvent('chatbot_event', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'chatbot_trigger',
      timestamp: Date.now(),
      user_id: user?.cunstomerId,
    });
    setIsVisible((prev) => !prev);
  };

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  if (
    pathName.includes('/order') ||
    pathName === ROUTES.blog ||
    pathName.includes('/amori') ||
    pathName.includes('/order-list')
  ) {
    return null;
  }
  return (
    <>
      {!isMobile ? (
        <div
          id="webie-container"
          className={cn('webie-namespace', {
            closed: !isVisible,
            expanded: isExpanded,
          })}
        >
          <div id="webie-messenger-frame">
            <div id="webie-iframe" className="flex flex-col h-full">
              <ChatHeader />
              {isAuthenticated && threadId ? (
                <Chat key={threadId} threadId={threadId} className="flex-1" />
              ) : isLoading ? (
                <div className="flex items-center justify-center h-full text-lg text-blue-60">
                  Đang kiểm tra đăng nhập...
                </div>
              ) : (
                <UnAuthen />
              )}
            </div>
          </div>
        </div>
      ) : (
        <Sheet open={isVisible} onOpenChange={toggleChat}>
          <SheetContent
            side="bottom"
            className="flex flex-col h-full p-0 border-none gap-0"
          >
            <SheetTitle>
              <ChatHeader />
            </SheetTitle>
            {isAuthenticated && threadId ? (
              <Chat key={threadId} threadId={threadId} className="flex-1" />
            ) : isLoading ? (
              <div className="flex items-center justify-center h-full text-lg text-blue-60">
                Đang kiểm tra đăng nhập...
              </div>
            ) : (
              <UnAuthen />
            )}
          </SheetContent>
        </Sheet>
      )}
      <div
        className={cn(
          'fixed top-1/2 -translate-y-1/2 md:right-5 right-1 z-50',
          (isVisible ||
            pathName.includes('/order') ||
            pathName.includes('/store/') ||
            pathName.includes('/m') ||
            isMobile ||
            pathName.includes('/order-wv')) &&
            'hidden'
        )}
      >
        <motion.div
          animate={{
            boxShadow: [
              '0 0 0 0 rgba(0, 122, 255, 0.5)',
              '0 0 0 12px rgba(0, 111, 255, 0)',
              '0 0 0 0 rgba(0, 122, 255, 0)',
            ],
          }}
          transition={{
            repeat: Infinity,
            duration: 2,
            ease: 'easeOut',
          }}
          className="rounded-full"
        >
          <Button
            onClick={toggleChat}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={cn(
              'size-14 p-1 rounded-full bg-gradient-to-br from-blue-50 to-secondary-70 text-white shadow-lg hover:shadow-xl relative flex items-center justify-center transition-all duration-200'
            )}
          >
            <Avatar className="size-10">
              <AvatarImage
                src={`${assetPrefix}/static/images/robot-chat-new.webp`}
                alt="AI Chat"
              />
              <AvatarFallback className="rounded-none size-10">
                AI
              </AvatarFallback>
            </Avatar>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={isHovered ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
              transition={{ duration: 0.2 }}
              className="absolute -top-8 right-1/2 translate-x-1/2 bg-white text-black text-xs px-2 py-1 rounded shadow"
            >
              Đặt đơn cùng Amori?
            </motion.div>
          </Button>
        </motion.div>
      </div>
      <LoginDialog
        open={showLoginDialog}
        onOpenChange={setShowLoginDialog}
        location="bot-chat-login"
      />
    </>
  );
}
